import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboard, getWeeklySummaries, deleteChild, updateChild } from '../../services/api';
import { StepAddChild } from './OnboardingWizard';
import SignOutButton from '../../components/SignOutButton';

// ─── Brand tokens (parent interface) ──────────────────────────────────────────

const C = {
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceMuted: '#F8FAFC',
  surfaceSubtle: '#F1F5F9',
  text: '#1E293B',
  textMuted: '#64748B',
  textSubtle: '#94A3B8',
  accent: '#0066FF',
  accentDark: '#0052CC',
  accentSoft: '#EFF4FF',
  accentBorder: '#C7D7FE',
  border: '#E2E8F0',
  borderStrong: '#CBD5E1',
  green: '#10B981',
  greenSoft: '#ECFDF5',
  greenBorder: '#A7F3D0',
  greenText: '#065F46',
  amber: '#F59E0B',
  amberSoft: '#FEF3C7',
  amberBorder: '#FDE68A',
  amberText: '#92400E',
  red: '#EF4444',
  redSoft: '#FEF2F2',
  redBorder: '#FECACA',
  redText: '#B91C1C',
  skeletonBase: '#F1F5F9',
  skeletonHighlight: '#E2E8F0',
};

const HEADING_FONT = "'Syne', sans-serif";
const BODY_FONT = "'DM Sans', sans-serif";

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, tone = 'neutral', icon }) {
  const tones = {
    neutral: { bg: C.surface, border: C.border,        value: C.text,       accent: C.accent, accentBg: C.accentSoft },
    amber:   { bg: C.surface, border: C.amberBorder,   value: C.amberText,  accent: C.amber,  accentBg: C.amberSoft },
    red:     { bg: C.surface, border: C.redBorder,     value: C.redText,    accent: C.red,    accentBg: C.redSoft },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <div style={{
      background: t.bg,
      border: `1px solid ${t.border}`,
      borderRadius: 12,
      padding: '18px 20px',
      flex: '1 1 180px',
      minWidth: 180,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: t.accentBg,
          color: t.accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>{icon}</div>
        {tone !== 'neutral' && (
          <span style={{
            fontFamily: BODY_FONT,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: t.accent,
            background: t.accentBg,
            padding: '3px 8px',
            borderRadius: 999,
          }}>Needs review</span>
        )}
      </div>
      <div>
        <div style={{
          fontFamily: HEADING_FONT,
          fontSize: 32,
          fontWeight: 700,
          color: t.value,
          letterSpacing: '-0.02em',
          lineHeight: 1,
        }}>{value}</div>
        <div style={{
          fontFamily: BODY_FONT,
          fontSize: 13,
          color: C.textMuted,
          marginTop: 6,
        }}>{label}</div>
      </div>
    </div>
  );
}

function ChildAvatar({ name }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  return (
    <div style={{
      width: 44,
      height: 44,
      borderRadius: 12,
      background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentDark} 100%)`,
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: HEADING_FONT,
      fontWeight: 700,
      fontSize: 18,
      letterSpacing: '-0.01em',
      boxShadow: '0 2px 6px rgba(0, 102, 255, 0.25)',
      flexShrink: 0,
    }}>{initial}</div>
  );
}

function SubjectPill({ children, tone = 'blue' }) {
  const tones = {
    blue:  { bg: C.accentSoft, color: C.accentDark, border: C.accentBorder },
    green: { bg: C.greenSoft,  color: C.greenText,  border: C.greenBorder },
  };
  const t = tones[tone];
  return (
    <span style={{
      background: t.bg,
      color: t.color,
      border: `1px solid ${t.border}`,
      borderRadius: 999,
      padding: '3px 10px',
      fontSize: 12,
      fontWeight: 500,
      fontFamily: BODY_FONT,
      lineHeight: 1.4,
    }}>{children}</span>
  );
}

function ChildLoginLink({ familyId }) {
  const [copied, setCopied] = useState(false);
  // Reads from window.location.origin so dev (http://localhost:3000) and any
  // production host both produce a working link without configuration.
  const link = `${window.location.origin}/login?familyId=${familyId}`;

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard write can fail in non-secure contexts or if the user denied
      // permission. Fall through silently — the link is visible to copy by hand.
    }
  }

  return (
    <div style={{
      background: C.surfaceMuted,
      borderTop: `1px solid ${C.border}`,
      padding: '14px 22px 16px',
      fontFamily: BODY_FONT,
    }}>
      <div style={{
        fontFamily: HEADING_FONT,
        fontSize: 12,
        fontWeight: 600,
        color: C.textMuted,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: 8,
      }}>
        Child login link
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <code
          title={link}
          style={{
            flex: 1,
            minWidth: 0,
            padding: '8px 12px',
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 12.5,
            color: C.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {link}
        </code>
        <button
          type="button"
          onClick={handleCopy}
          aria-live="polite"
          style={{
            fontFamily: BODY_FONT,
            fontSize: 13,
            fontWeight: 600,
            padding: '8px 12px',
            background: copied ? C.greenSoft : '#fff',
            color: copied ? C.greenText : C.accent,
            border: `1px solid ${copied ? C.greenBorder : C.accentBorder}`,
            borderRadius: 8,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            whiteSpace: 'nowrap',
            transition: 'background 160ms ease, border-color 160ms ease, color 160ms ease',
            flexShrink: 0,
          }}
        >
          {copied ? (
            <>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <rect x="3.5" y="3.5" width="7" height="8" rx="1" stroke="currentColor" strokeWidth="1.4" />
                <path d="M5.5 3.5V2.5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
              Copy link
            </>
          )}
        </button>
      </div>
      <p style={{
        margin: '10px 0 0',
        fontSize: 12,
        color: C.textMuted,
        lineHeight: 1.5,
      }}>
        Send this link to your child's device — they only need to open it once. After adding Lantern to their home screen, it will remember their family automatically.
      </p>
    </div>
  );
}

function WeeklySummaryCard({ child }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    getWeeklySummaries(child.id)
      .then(r => setData(r.data))
      .catch(() => {});
  }, [child.id]);

  if (!data) return null;

  const { currentWeek, summaries } = data;
  const latestSummary = summaries[0];

  return (
    <div style={{
      background: C.surfaceMuted,
      borderTop: `1px solid ${C.border}`,
      padding: '18px 22px 20px',
      fontFamily: BODY_FONT,
    }}>
      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
        {/* This week */}
        <div style={{ flex: '1 1 220px' }}>
          <div style={{
            fontSize: 11,
            color: C.textSubtle,
            marginBottom: 8,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 600,
          }}>This week</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 10 }}>
            <span style={{
              fontFamily: HEADING_FONT,
              fontWeight: 700,
              fontSize: 22,
              color: C.text,
              letterSpacing: '-0.02em',
            }}>{currentWeek.totalSessions}</span>
            <span style={{ fontSize: 13, color: C.textMuted }}>
              session{currentWeek.totalSessions !== 1 ? 's' : ''} · {currentWeek.totalMinutes} min
            </span>
          </div>
          {currentWeek.subjects.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {currentWeek.subjects.map(s => (
                <SubjectPill key={s} tone="blue">{s}</SubjectPill>
              ))}
            </div>
          )}
        </div>

        {/* Last week */}
        {latestSummary && (
          <div style={{ flex: '2 1 320px' }}>
            <div style={{
              fontSize: 11,
              color: C.textSubtle,
              marginBottom: 8,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontWeight: 600,
            }}>Last week</div>
            <p style={{
              fontSize: 13,
              color: C.text,
              lineHeight: 1.6,
              margin: '0 0 10px',
              textWrap: 'pretty',
            }}>{latestSummary.summary_text}</p>
            {latestSummary.subjects_covered.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {latestSummary.subjects_covered.map(s => (
                  <SubjectPill key={s} tone="green">{s}</SubjectPill>
                ))}
                <span style={{ fontSize: 12, color: C.textSubtle, marginLeft: 4 }}>
                  · {latestSummary.total_sessions} sessions · {latestSummary.total_minutes} min
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {summaries.length === 0 && currentWeek.totalSessions === 0 && (
        <p style={{ fontSize: 13, color: C.textSubtle, margin: 0 }}>
          No sessions yet this week.
        </p>
      )}
    </div>
  );
}

function SkeletonBlock({ width, height, style }) {
  return (
    <div
      aria-hidden="true"
      style={{
        background: C.skeletonBase,
        backgroundImage: `linear-gradient(90deg, ${C.skeletonBase} 0%, ${C.skeletonHighlight} 50%, ${C.skeletonBase} 100%)`,
        backgroundSize: '200% 100%',
        animation: 'db-shimmer 1.4s ease-in-out infinite',
        borderRadius: 6,
        width,
        height,
        ...style,
      }}
    />
  );
}

function DashboardSkeleton() {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <SkeletonBlock width={240} height={32} />
        <SkeletonBlock width={140} height={32} />
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 32 }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            padding: '18px 20px',
            flex: '1 1 180px',
            minWidth: 180,
          }}>
            <SkeletonBlock width={32} height={32} style={{ marginBottom: 14, borderRadius: 8 }} />
            <SkeletonBlock width={68} height={28} style={{ marginBottom: 8 }} />
            <SkeletonBlock width={110} height={12} />
          </div>
        ))}
      </div>
      <SkeletonBlock width={140} height={20} style={{ marginBottom: 16 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {[0, 1].map(i => (
          <div key={i} style={{
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            overflow: 'hidden',
          }}>
            <div style={{ padding: '16px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <SkeletonBlock width={44} height={44} style={{ borderRadius: 12 }} />
                <div>
                  <SkeletonBlock width={120} height={16} style={{ marginBottom: 6 }} />
                  <SkeletonBlock width={160} height={12} />
                </div>
              </div>
              <SkeletonBlock width={120} height={36} style={{ borderRadius: 8 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconSessions = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3 4.5C3 3.67 3.67 3 4.5 3h7c.83 0 1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5H7l-3 2.5V11h-.5A1.5 1.5 0 0 1 2 9.5v-5C2 4.5 3 4.5 3 4.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
  </svg>
);

const IconMessages = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const IconShield = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 1.5l5 2v4c0 3-2.2 5.5-5 6.5-2.8-1-5-3.5-5-6.5v-4l5-2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
  </svg>
);

const IconArrow = (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function LanternBrand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentDark} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(0, 102, 255, 0.3)',
      }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <rect x="5" y="3" width="6" height="8" rx="1" stroke="#fff" strokeWidth="1.4"/>
          <path d="M6.5 3V2h3v1M5 11h6M7 13h2" stroke="#fff" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
      </div>
      <span style={{
        fontFamily: HEADING_FONT,
        fontSize: 17,
        fontWeight: 700,
        color: C.text,
        letterSpacing: '-0.01em',
      }}>Lantern</span>
    </div>
  );
}

// ─── Main dashboard ───────────────────────────────────────────────────────────

export default function Dashboard() {
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);
  const [addChildOpen, setAddChildOpen] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [unlockCandidate, setUnlockCandidate] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState(null);
  const navigate = useNavigate();

  const loadDashboard = useCallback(() => {
    getDashboard()
      .then(r => setData(r.data))
      .catch((err) => {
        if (err.response?.status === 401) {
          navigate('/parent/login');
          return;
        }
        setError('Could not load dashboard. Please try again.');
      });
  }, [navigate]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  function closeAddChild() {
    setAddChildOpen(false);
    loadDashboard();
  }

  async function confirmDeleteChild() {
    if (!deleteCandidate || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteChild(deleteCandidate.id);
      setDeleteCandidate(null);
      loadDashboard();
    } catch (err) {
      setDeleteError(err?.response?.data?.error || 'Could not remove this child. Please try again.');
    } finally {
      setDeleting(false);
    }
  }

  function cancelDeleteChild() {
    if (deleting) return;
    setDeleteCandidate(null);
    setDeleteError(null);
  }

  async function confirmUnlockChild() {
    if (!unlockCandidate || unlocking) return;
    setUnlocking(true);
    setUnlockError(null);
    try {
      await updateChild(unlockCandidate.id, { locked: false });
      setUnlockCandidate(null);
      loadDashboard();
    } catch (err) {
      setUnlockError(err?.response?.data?.error || 'Could not unlock this child. Please try again.');
    } finally {
      setUnlocking(false);
    }
  }

  function cancelUnlockChild() {
    if (unlocking) return;
    setUnlockCandidate(null);
    setUnlockError(null);
  }

  const lastActivity = data?.lastActivity
    ? new Date(data.lastActivity).toLocaleString(undefined, {
        weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
      })
    : 'No activity yet';

  return (
    <>
      <style>{`
        @keyframes db-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .db-row-btn:hover { background: ${C.accentDark}; }
        .db-alert-pill:hover { background: ${C.redSoft}; }
        .db-remove-btn:hover { background: ${C.redSoft}; border-color: ${C.redBorder}; color: ${C.redText}; }
        .db-remove-btn:focus-visible { outline: none; box-shadow: 0 0 0 4px ${C.redSoft}; border-color: ${C.redText}; }
        .db-settings-btn:hover { background: ${C.accentSoft}; border-color: ${C.accentBorder}; color: ${C.accent}; }
        .db-settings-btn:focus-visible { outline: none; box-shadow: 0 0 0 4px ${C.accentSoft}; border-color: ${C.accent}; }
      `}</style>
      <div style={{
        minHeight: '100vh',
        background: C.bg,
        fontFamily: BODY_FONT,
        color: C.text,
      }}>
        {/* Top nav */}
        <div style={{
          borderBottom: `1px solid ${C.border}`,
          background: '#fff',
          position: 'sticky',
          top: 0,
          zIndex: 5,
        }}>
          <div style={{
            maxWidth: 980,
            margin: '0 auto',
            padding: '14px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <LanternBrand />
            <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
              <button
                type="button"
                onClick={() => navigate('/parent/data-controls')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  color: C.textMuted,
                  fontFamily: BODY_FONT,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >Your data</button>
              <SignOutButton />
            </div>
          </div>
        </div>

        {/* Main */}
        <div style={{ maxWidth: 980, margin: '0 auto', padding: '32px 24px 56px' }}>
          {error ? (
            <div style={{
              background: C.redSoft,
              border: `1px solid ${C.redBorder}`,
              borderRadius: 10,
              padding: '14px 18px',
              color: C.redText,
              fontSize: 14,
            }}>{error}</div>
          ) : !data ? (
            <DashboardSkeleton />
          ) : (
            <>
              {/* Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 16,
                flexWrap: 'wrap',
                marginBottom: 28,
              }}>
                <div>
                  <h1 style={{
                    margin: 0,
                    fontFamily: HEADING_FONT,
                    fontSize: 30,
                    fontWeight: 700,
                    color: C.text,
                    letterSpacing: '-0.025em',
                    lineHeight: 1.15,
                  }}>Parent Dashboard</h1>
                  <p style={{
                    margin: '6px 0 0',
                    fontSize: 14,
                    color: C.textMuted,
                  }}>
                    Last activity: <span style={{ color: C.text, fontWeight: 500 }}>{lastActivity}</span>
                  </p>
                </div>
                {data.unreviewedFlagsCount > 0 && (
                  <button
                    className="db-alert-pill"
                    onClick={() => navigate('/parent/safeguarding')}
                    style={{
                      background: '#fff',
                      color: C.redText,
                      border: `1px solid ${C.redBorder}`,
                      borderRadius: 999,
                      padding: '8px 14px',
                      fontFamily: BODY_FONT,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      transition: 'background 160ms ease',
                    }}
                  >
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: C.red,
                      boxShadow: `0 0 0 4px ${C.redSoft}`,
                    }} />
                    {data.unreviewedFlagsCount} unreviewed alert{data.unreviewedFlagsCount !== 1 ? 's' : ''}
                    {IconArrow}
                  </button>
                )}
              </div>

              {/* 2FA setup prompt — only when the parent hasn't enabled it. */}
              {data.twoFactorEnabled === false && (
                <div role="region" aria-label="Two-factor setup recommendation" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 18px',
                  marginBottom: 24,
                  background: '#FEF3C7',
                  border: '1px solid #FCD34D',
                  borderRadius: 12,
                  color: '#92400E',
                }}>
                  <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1 }}>🔒</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      Protect your account — set up two-factor authentication
                    </div>
                    <div style={{ fontSize: 13, marginTop: 2, color: '#78350F' }}>
                      Adds a second step at sign-in using an authenticator app on your phone.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/parent/2fa/setup')}
                    style={{
                      padding: '9px 14px',
                      background: '#92400E',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontFamily: BODY_FONT,
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >Set up now</button>
                </div>
              )}

              {/* Stats */}
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
                <StatCard label="Total sessions" value={data.totalSessions} icon={IconSessions} tone="neutral" />
                <StatCard label="Total messages" value={data.totalMessages} icon={IconMessages} tone="neutral" />
                <StatCard
                  label="Safeguarding alerts"
                  value={data.safeguardingFlagsCount}
                  icon={IconShield}
                  tone={data.unreviewedFlagsCount > 0 ? 'amber' : 'neutral'}
                />
              </div>

              {/* AI disclaimer — sits between the stats and the children list so
                  parents see it on every dashboard view without it competing
                  with the safeguarding card for attention. */}
              <p style={{
                margin: '0 0 28px',
                paddingTop: 14,
                borderTop: `1px solid ${C.border}`,
                fontSize: 12,
                lineHeight: 1.55,
                color: C.textMuted,
              }}>
                Lantern is designed to guide your child's thinking and we've built in safeguards to improve accuracy. Like all AI, it can occasionally make mistakes — if something doesn't look right, it's always worth checking. Used alongside good teaching, Lantern can make a real difference to your child's learning.
              </p>

              {/* Children */}
              <div style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                marginBottom: 14,
              }}>
                <h2 style={{
                  fontFamily: HEADING_FONT,
                  fontSize: 18,
                  fontWeight: 600,
                  color: C.text,
                  margin: 0,
                  letterSpacing: '-0.01em',
                }}>
                  Children <span style={{ color: C.textSubtle, fontWeight: 500 }}>({data.children.length})</span>
                </h2>
                <button
                  type="button"
                  onClick={() => setAddChildOpen(true)}
                  style={{
                    fontFamily: BODY_FONT,
                    fontSize: 13,
                    fontWeight: 600,
                    padding: '8px 14px',
                    background: C.accent,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M7 3v8M3 7h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  Add child
                </button>
              </div>

              {data.children.length === 0 ? (
                <p style={{ color: C.textMuted, fontSize: 14 }}>No children added yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {data.children.map(child => (
                    <div key={child.id} style={{
                      background: C.surface,
                      border: `1px solid ${C.border}`,
                      borderRadius: 12,
                      overflow: 'hidden',
                      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                    }}>
                      <div style={{
                        padding: '16px 22px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 16,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                          <ChildAvatar name={child.display_name} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{
                              fontWeight: 600,
                              color: C.text,
                              fontSize: 15,
                              fontFamily: BODY_FONT,
                              letterSpacing: '-0.005em',
                            }}>{child.display_name}</div>
                            <div style={{
                              fontSize: 13,
                              color: C.textMuted,
                              marginTop: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              flexWrap: 'wrap',
                            }}>
                              <span>@{child.username}</span>
                              <span style={{ color: C.textSubtle }}>·</span>
                              <span>Year {child.year_group}</span>
                              {child.pinLocked && (
                                <>
                                  <span style={{ color: C.textSubtle }}>·</span>
                                  <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    padding: '2px 8px',
                                    borderRadius: 999,
                                    background: C.redSoft,
                                    color: C.redText,
                                    border: `1px solid ${C.redBorder}`,
                                    fontSize: 12,
                                    fontWeight: 700,
                                  }}>
                                    <span aria-hidden="true">🔒</span>
                                    Locked
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          {child.pinLocked && (
                            <button
                              type="button"
                              onClick={() => setUnlockCandidate(child)}
                              style={{
                                fontFamily: BODY_FONT,
                                fontSize: 13,
                                fontWeight: 600,
                                padding: '9px 14px',
                                borderRadius: 8,
                                background: '#fff',
                                color: C.redText,
                                border: `1.5px solid ${C.redBorder}`,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                              }}
                            >Unlock account</button>
                          )}
                          <button
                            className="db-row-btn"
                            onClick={() => navigate(`/parent/sessions/${child.id}`)}
                            style={{
                              fontFamily: BODY_FONT,
                              fontSize: 13,
                              fontWeight: 600,
                              padding: '9px 16px',
                              borderRadius: 8,
                              background: C.accent,
                              color: '#fff',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              transition: 'background 160ms ease',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            View sessions
                            {IconArrow}
                          </button>
                          <button
                            type="button"
                            className="db-settings-btn"
                            onClick={() => navigate(`/parent/children/${child.id}/settings`)}
                            aria-label={`Settings for ${child.display_name}`}
                            title={`Settings for ${child.display_name}`}
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 8,
                              background: '#fff',
                              color: C.textMuted,
                              border: `1px solid ${C.border}`,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'background 160ms ease, border-color 160ms ease, color 160ms ease',
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                              <circle cx="7" cy="7" r="2" stroke="currentColor" strokeWidth="1.4"/>
                              <path d="M7 1.5v1.7M7 10.8v1.7M2.6 4.5l1.5.8M9.9 8.7l1.5.8M2.6 9.5l1.5-.8M9.9 5.3l1.5-.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="db-remove-btn"
                            onClick={() => setDeleteCandidate(child)}
                            aria-label={`Remove ${child.display_name}`}
                            title={`Remove ${child.display_name}`}
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 8,
                              background: '#fff',
                              color: C.textMuted,
                              border: `1px solid ${C.border}`,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'background 160ms ease, border-color 160ms ease, color 160ms ease',
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                              <path d="M2.5 3.5h9M5 3.5V2.5h4v1M3.5 3.5l.5 8a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1l.5-8M5.5 5.5v5M8.5 5.5v5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <WeeklySummaryCard child={child} />
                      <ChildLoginLink familyId={child.family_id} />
                    </div>
                  ))}
                </div>
              )}

              {/* All-alerts secondary button */}
              {data.safeguardingFlagsCount > 0 && (
                <div style={{ marginTop: 32 }}>
                  <button
                    className="db-alert-pill"
                    onClick={() => navigate('/parent/safeguarding')}
                    style={{
                      fontFamily: BODY_FONT,
                      fontSize: 13,
                      fontWeight: 600,
                      padding: '8px 14px',
                      background: '#fff',
                      color: C.redText,
                      border: `1px solid ${C.redBorder}`,
                      borderRadius: 999,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      transition: 'background 160ms ease',
                    }}
                  >
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: C.red,
                      boxShadow: `0 0 0 4px ${C.redSoft}`,
                    }} />
                    View all safeguarding alerts ({data.safeguardingFlagsCount})
                    {IconArrow}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add child modal — reuses the wizard's StepAddChild form */}
      {addChildOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Add a child"
          onClick={closeAddChild}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '40px 20px',
            zIndex: 100,
            overflowY: 'auto',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 16,
              padding: '28px 28px 32px',
              width: '100%',
              maxWidth: 520,
              boxShadow: '0 20px 50px rgba(15,23,42,0.18)',
              fontFamily: BODY_FONT,
              position: 'relative',
            }}
          >
            <button
              type="button"
              onClick={closeAddChild}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: 14,
                right: 14,
                width: 32,
                height: 32,
                borderRadius: 8,
                border: 'none',
                background: 'transparent',
                color: C.textMuted,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
            <StepAddChild onNext={closeAddChild} />
          </div>
        </div>
      )}

      {/* Remove-child confirmation modal */}
      {deleteCandidate && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="remove-child-title"
          aria-describedby="remove-child-desc"
          onClick={cancelDeleteChild}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            zIndex: 100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 16,
              padding: '24px 26px',
              width: '100%',
              maxWidth: 440,
              boxShadow: '0 20px 50px rgba(15,23,42,0.18)',
              fontFamily: BODY_FONT,
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 14,
            }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: C.redSoft,
                color: C.red,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                  <path d="M9 2 L17 16 H1 Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                  <path d="M9 7v4M9 13v.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </div>
              <h2 id="remove-child-title" style={{
                margin: 0,
                fontFamily: HEADING_FONT,
                fontSize: 18,
                fontWeight: 700,
                color: C.text,
                letterSpacing: '-0.01em',
              }}>
                Remove {deleteCandidate.display_name}?
              </h2>
            </div>
            <p id="remove-child-desc" style={{
              margin: 0,
              fontSize: 14,
              color: C.textMuted,
              lineHeight: 1.55,
            }}>
              This will delete <strong style={{ color: C.text }}>{deleteCandidate.display_name}</strong>'s account along with all their sessions and messages, and cannot be undone.
            </p>

            {deleteError && (
              <div role="alert" style={{
                marginTop: 14,
                padding: '10px 14px',
                background: C.redSoft,
                border: `1px solid ${C.redBorder}`,
                borderRadius: 10,
                color: C.redText,
                fontSize: 13,
                fontWeight: 500,
              }}>
                {deleteError}
              </div>
            )}

            <div style={{
              marginTop: 22,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}>
              <button
                type="button"
                onClick={cancelDeleteChild}
                disabled={deleting}
                style={{
                  fontFamily: BODY_FONT,
                  fontSize: 14,
                  fontWeight: 600,
                  padding: '10px 16px',
                  background: '#fff',
                  color: C.text,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteChild}
                disabled={deleting}
                style={{
                  fontFamily: BODY_FONT,
                  fontSize: 14,
                  fontWeight: 600,
                  padding: '10px 16px',
                  background: C.red,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.7 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {deleting ? 'Removing…' : 'Yes, remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {unlockCandidate && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="unlock-child-title"
          aria-describedby="unlock-child-desc"
          onClick={cancelUnlockChild}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            zIndex: 100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 16,
              padding: '24px 26px',
              width: '100%',
              maxWidth: 440,
              boxShadow: '0 20px 50px rgba(15,23,42,0.18)',
              fontFamily: BODY_FONT,
            }}
          >
            <h2 id="unlock-child-title" style={{
              margin: '0 0 8px',
              fontFamily: HEADING_FONT,
              fontSize: 18,
              fontWeight: 700,
              color: C.text,
              letterSpacing: '-0.01em',
            }}>Unlock {unlockCandidate.display_name}'s account?</h2>
            <p id="unlock-child-desc" style={{
              margin: '0 0 16px',
              fontSize: 14,
              color: C.textMuted,
              lineHeight: 1.55,
            }}>
              This clears their failed-attempt count so they can try their PIN again. If they keep getting it wrong they'll be locked out again.
            </p>

            {unlockError && (
              <div role="alert" style={{
                marginBottom: 14,
                padding: '10px 14px',
                background: C.redSoft,
                border: `1px solid ${C.redBorder}`,
                borderRadius: 10,
                color: C.redText,
                fontSize: 14,
              }}>{unlockError}</div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button"
                onClick={cancelUnlockChild}
                disabled={unlocking}
                style={{
                  fontFamily: BODY_FONT,
                  fontSize: 14,
                  fontWeight: 600,
                  padding: '10px 16px',
                  background: '#fff',
                  color: C.text,
                  border: `1.5px solid ${C.border}`,
                  borderRadius: 8,
                  cursor: unlocking ? 'wait' : 'pointer',
                }}
              >Cancel</button>
              <button
                type="button"
                onClick={confirmUnlockChild}
                disabled={unlocking}
                style={{
                  fontFamily: BODY_FONT,
                  fontSize: 14,
                  fontWeight: 600,
                  padding: '10px 16px',
                  background: C.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: unlocking ? 'not-allowed' : 'pointer',
                  opacity: unlocking ? 0.7 : 1,
                }}
              >{unlocking ? 'Unlocking…' : 'Unlock account'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
